/**
 * modes/walker-paths.js
 * Quỹ đạo toán học thuần túy (Deterministic Trajectory) cho chế độ NPC Đi Dạo (Walker Mode).
 * Cung cấp hàm samplePath tính toán tọa độ (x, z) và hướng mặt (yaw) theo hàm thuần của thời gian t.
 */

export const WALKER_PATH_CONFIG_DESKTOP = {
  a: 1.16, // Bán trục X (mét) - phương ngang kéo dài để đạt span >= 65% trên desktop
  b: 0.9, // Bán trục Z (mét) - phương sâu (a > b: elip nằm ngang)
  omega: 0.7, // Vận tốc góc (rad/s) -> v in [0.630, 0.812] m/s
  period: (2 * Math.PI) / 0.7 // Chu kỳ T ≈ 8.976s
};

export const WALKER_PATH_CONFIG_COMPACT = {
  a: 0.58, // Bán trục X (mét) - tối ưu cho viewport đứng mobile
  b: 0.82, // Bán trục Z (mét)
  omega: 1.05, // Vận tốc góc (rad/s) -> v in [0.609, 0.861] m/s
  period: (2 * Math.PI) / 1.05 // Chu kỳ T ≈ 5.984s
};

export const WALKER_PATH_CONFIG = WALKER_PATH_CONFIG_DESKTOP;

/**
 * Tính toán vị trí (x, z) và hướng quay mặt (yaw) của NPC tại thời điểm t.
 * @param {string|object} pathDef - 'npc1' | 'npc2' hoặc { pathId: string, phaseOffset?: number }
 * @param {number} t - Thời gian tính bằng giây
 * @param {boolean} isCompact - Bố cục compact (mobile) hay desktop
 * @returns {{ x: number, z: number, yaw: number }}
 */
export function samplePath(pathDef, t = 0, isCompact = false) {
  const config = isCompact ? WALKER_PATH_CONFIG_COMPACT : WALKER_PATH_CONFIG_DESKTOP;
  const { a, b, omega } = config;
  const pathId = typeof pathDef === 'string' ? pathDef : pathDef?.pathId || 'npc1';
  const customPhase = typeof pathDef === 'object' && pathDef?.phaseOffset !== undefined ? pathDef.phaseOffset : null;

  // NPC 2 lệch pha nửa vòng (PI rad) so với NPC 1 để luôn giữ khoảng cách an toàn đối xứng
  const basePhase = pathId === 'npc2' ? Math.PI : 0;
  const phase = customPhase !== null ? customPhase : basePhase;

  const angle = omega * t + phase;
  const x = a * Math.cos(angle);
  const z = b * Math.sin(angle);

  // Vector tiếp tuyến quỹ đạo: dx/dt = -a * omega * sin(angle), dz/dt = b * omega * cos(angle)
  const vx = -a * omega * Math.sin(angle);
  const vz = b * omega * Math.cos(angle);

  // Avatar nhìn theo trục +Z khi rotation.y = 0.
  // Tiếp tuyến chuyển động là (vx, 0, vz) nên yaw = Math.atan2(vx, vz).
  const yaw = Math.atan2(vx, vz);

  return { x, z, yaw };
}
